import { useMemo } from 'react'

import { useQueryResult, useWindow } from '@krp/client-runtime'
import {
  CreateDefaultsScreen,
  createDefaultChanged,
  createDefaultsReset,
  createDefaultsValues,
  makeSelectCreateDefaultsVm,
  normalizeRid,
  onecCreateDefaults,
  onecCreateDefaultsSave,
  reloadCreateDefaults,
  type CreateDefaultsActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'

/**
 * Binding окна «Создание по умолчанию» базы 1С (RP-SYN11): rid — из окна
 * (D-064). Поля и справочники — один запрос службы; черновик — срез
 * `onec.createDefaults`; «Сохранить» отправляет значения всех полей окна.
 */
export function CreateDefaultsBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const args = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const query = useQueryResult(onecCreateDefaults, args)
  const intent = useAppSelector((state) => state.onec.createDefaults)
  const [save, saveState] = onecCreateDefaultsSave.useMutation({ fixedCacheKey: `onec-create-defaults-save:${resourceId}` })

  const selectVm = useMemo(() => makeSelectCreateDefaultsVm(), [])
  const vm = selectVm({ resourceId, query, intent, save: saveState })

  const actions = useMemo<CreateDefaultsActions>(
    () => ({
      changed: (key, value) => dispatch(createDefaultChanged({ resourceId, key, value })),
      save: () => {
        if (resourceId === '' || !vm.dirty) return
        void save({ resourceId, values: createDefaultsValues(vm.fields) })
      },
      reset: () => dispatch(createDefaultsReset({ resourceId })),
      retry: () => dispatch(reloadCreateDefaults(resourceId)),
    }),
    [dispatch, resourceId, save, vm.dirty, vm.fields],
  )

  return <CreateDefaultsScreen vm={vm} actions={actions} />
}
